import { decodeBase64, encodeBase64 } from 'vault-crypto';

import { JsonCompatible } from './types';

const FLAG_UINT8_ARRAY = 'FLAG_UINT8_ARRAY';
const FLAG_ARRAY_BUFFER = 'FLAG_ARRAY_BUFFER';
const FLAG_DATE = 'FLAG_DATE';
const FLAG_UNDEFINED = 'FLAG_UNDEFINED';
// The same two binary types, but with the bytes kept out of the json and referenced by index, so
// a container format can store them as-is instead of base64ed into the json.
const FLAG_UINT8_ARRAY_REF = 'FLAG_UINT8_ARRAY_REF';
const FLAG_ARRAY_BUFFER_REF = 'FLAG_ARRAY_BUFFER_REF';

// Converts a single flag-wrapped node back to its runtime type. Returns the
// sentinel NOT_A_FLAG for anything that isn't a recognized flag object, so the
// caller can tell "this node is a flag" apart from "this flag legitimately
// thaws to undefined" (FLAG_UNDEFINED).
const NOT_A_FLAG = Symbol('NOT_A_FLAG');

// A ref flag carries none of its own bytes, so a reader without the blobs cannot reconstruct the
// value. Returning NOT_A_FLAG would route it into the unrecognized-flag fallback below and hand
// the marker object out as if it were data.
function requireResolveBlob(resolveBlob: ResolveBlob | undefined): ResolveBlob {
  if (!resolveBlob) {
    throw new Error('Encountered a hoisted binary value without the blobs to resolve it!');
  }
  return resolveBlob;
}

function thawFlag(value: { flag: unknown; data?: any }, resolveBlob?: ResolveBlob): unknown {
  switch (value.flag) {
    case FLAG_UINT8_ARRAY:
      return decodeBase64(value.data);
    case FLAG_ARRAY_BUFFER:
      return decodeBase64(value.data).buffer;
    case FLAG_UINT8_ARRAY_REF:
      return requireResolveBlob(resolveBlob)(value.data);
    case FLAG_ARRAY_BUFFER_REF:
      return requireResolveBlob(resolveBlob)(value.data).buffer;
    case FLAG_DATE:
      return new Date(value.data);
    case FLAG_UNDEFINED:
      return undefined;
    default:
      return NOT_A_FLAG;
  }
}

// Post-parse replacement of flag-wrapped nodes, mutating the freshly-parsed
// graph in place. Used instead of a JSON.parse reviver: a reviver forces V8 to
// call back into JS for *every* node and disables the engine's fast internal
// parse, so plain JSON.parse followed by this targeted JS walk is several times
// faster.
function thawWalk(value: unknown, resolveBlob?: ResolveBlob): any {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if ('flag' in value) {
    const thawed = thawFlag(value, resolveBlob);
    if (thawed !== NOT_A_FLAG) {
      return thawed;
    }
    // Unrecognized flag: fall through and treat it as a plain object, so any
    // nested flags inside it are still thawed (the reviver would too).
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = thawWalk(value[i], resolveBlob);
    }
  } else {
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      obj[key] = thawWalk(obj[key], resolveBlob);
    }
  }
  return value;
}

// a replacer function for JSON.stringify
// note that we don't use the value parameter as per https://stackoverflow.com/a/28831665
export function jsonFreeze(this: any, key: string) {
  const value = this[key];
  if (value instanceof Uint8Array) {
    return {
      flag: FLAG_UINT8_ARRAY,
      data: encodeBase64(value),
    };
  }
  if (value instanceof ArrayBuffer) {
    return {
      flag: FLAG_ARRAY_BUFFER,
      data: encodeBase64(new Uint8Array(value)),
    };
  }
  if (value instanceof Date) {
    return {
      flag: FLAG_DATE,
      data: value.toISOString(),
    };
  }
  if (value === undefined) {
    return {
      flag: FLAG_UNDEFINED,
    };
  }
  return value;
}

export type JsonFreezeCompatible<T> = JsonCompatible<
  T,
  Uint8Array | ArrayBuffer | Date | undefined
>;

export function jsonStringifyFreeze<T>(
  value: JsonFreezeCompatible<T>,
  space?: string | number,
): string {
  return JSON.stringify(value, jsonFreeze, space);
}

export function jsonParseThaw(text: string): unknown {
  // Parse natively (fast), then walk the graph to replace flag-wrapped nodes,
  // rather than paying the per-node reviver callback cost during the parse.
  return thawWalk(JSON.parse(text));
}

// Returns a blob by index for jsonParseThawWithBlobs.
// - Must throw for an index that its storage doesn't hold
// - for ArrayBuffer refs, the returned array must span the whole buffer.
export type ResolveBlob = (index: number) => Uint8Array;

// Freezes as jsonStringifyFreeze, except that binary values are collected into `blobs` and
// referenced by index instead of base64ed into the json. The caller stores the blobs alongside
// the json, and passes them back to jsonParseThawWithBlobs in the same order.
export function jsonStringifyFreezeWithBlobs<T>(value: JsonFreezeCompatible<T>): {
  json: string;
  blobs: Uint8Array[];
} {
  const blobs: Uint8Array[] = [];
  function hoistingFreeze(this: any, key: string) {
    const entry = this[key];
    if (entry instanceof Uint8Array) {
      return { flag: FLAG_UINT8_ARRAY_REF, data: blobs.push(entry) - 1 };
    }
    if (entry instanceof ArrayBuffer) {
      return { flag: FLAG_ARRAY_BUFFER_REF, data: blobs.push(new Uint8Array(entry)) - 1 };
    }
    return jsonFreeze.call(this, key);
  }
  return { json: JSON.stringify(value, hoistingFreeze), blobs };
}

// Counterpart to jsonStringifyFreezeWithBlobs. See ResolveBlob for contract.
export function jsonParseThawWithBlobs(text: string, resolveBlob: ResolveBlob): unknown {
  return thawWalk(JSON.parse(text), resolveBlob);
}
